// src/pages/admin/AdminLMS.jsx — FINAL COMPLETE VERSION
// Uses create-lms-user Edge Function to create auth accounts

import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import AdminLayout from './AdminLayout';
import {
  Eye, FileText, Search, RefreshCw,
  Copy, CheckCircle, ExternalLink, AlertCircle,
} from 'lucide-react';

const STATUS_COLORS = {
  pending:      { bg:'#FFFBEB', color:'#92400E', border:'#FDE68A',  label:'Pending'      },
  under_review: { bg:'#EFF6FF', color:'#1D4ED8', border:'#BFDBFE',  label:'Under Review' },
  approved:     { bg:'#F0FDF4', color:'#065F46', border:'#BBF7D0',  label:'Approved'     },
  rejected:     { bg:'#FEF2F2', color:'#991B1B', border:'#FECACA',  label:'Rejected'     },
};

export default function AdminLMS() {
  const [accounts,     setAccounts]     = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [statusF,      setStatusF]      = useState('');
  const [selected,     setSelected]     = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [customPwd,    setCustomPwd]    = useState('');
  const [copied,       setCopied]       = useState('');
  const [success,      setSuccess]      = useState('');
  const [error,        setError]        = useState('');

  useEffect(() => { load(); }, []);

  useEffect(() => {
    let f = accounts;
    if (search.trim()) {
      const q = search.toLowerCase();
      f = f.filter(a =>
        (a.institution_name   || '').toLowerCase().includes(q) ||
        (a.contact_email      || '').toLowerCase().includes(q) ||
        (a.contact_name       || '').toLowerCase().includes(q) ||
        (a.lms_username       || '').toLowerCase().includes(q)
      );
    }
    if (statusF) f = f.filter(a => a.verification_status === statusF);
    setFiltered(f);
  }, [accounts, search, statusF]);

  const load = async () => {
    setLoading(true);
    try {
      // Get all accounts
      const { data: accs, error: err } = await supabase
        .from('institution_accounts')
        .select('*')
        .order('created_at', { ascending: false });

      if (err) { setError('DB error: ' + err.message); setLoading(false); return; }
      if (!accs || accs.length === 0) { setAccounts([]); setLoading(false); return; }

      // Get institution names
      const instIds = [...new Set(accs.map(a => a.institution_id).filter(Boolean))];
      let instMap = {};
      if (instIds.length > 0) {
        const { data: insts } = await supabase
          .from('institutions')
          .select('id,name,city,country,category')
          .in('id', instIds);
        if (insts) insts.forEach(i => { instMap[i.id] = i; });
      }

      const merged = accs.map(a => ({
        ...a,
        institution_name:     instMap[a.institution_id]?.name     || '—',
        institution_city:     instMap[a.institution_id]?.city     || '',
        institution_country:  instMap[a.institution_id]?.country  || '',
        institution_category: instMap[a.institution_id]?.category || '',
      }));

      setAccounts(merged);
    } catch(e) {
      setError('Load error: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#!';
    let pwd = '';
    for (let i = 0; i < 12; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
    return pwd;
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2500);
  };

  const handleApprove = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const pwd = customPwd.trim() || generatePassword();

      // Get admin session token
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Not authenticated as admin');

      // Call Edge Function to create auth user
      const { data, error: fnErr } = await supabase.functions.invoke('create-lms-user', {
        body: {
          account_id: selected.id,
          email:      selected.contact_email,
          password:   pwd,
          username:   selected.lms_username,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      if (fnErr) throw new Error(fnErr.message || 'Edge function error');
      if (!data?.success) throw new Error(data?.error || 'Failed to create auth user');

      setSuccess(`✅ Institution approved!\n\nUsername: ${selected.lms_username}\nPassword: ${pwd}\n\nEmail these to: ${selected.contact_email}`);

      await load();
      // Update selected state to show credentials
      setSelected(prev => ({
        ...prev,
        verification_status:    'approved',
        is_active:              true,
        assigned_username:      prev.lms_username,
        assigned_password_hint: pwd,
      }));
      setCustomPwd('');
    } catch(e) {
      setError('Approval failed: ' + (e.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !rejectReason.trim()) { setError('Please enter rejection reason'); return; }
    setSaving(true);
    setError('');
    try {
      await supabase.from('institution_accounts').update({
        verification_status: 'rejected',
        rejection_reason:    rejectReason.trim(),
        rejected_at:         new Date().toISOString(),
        is_active:           false,
      }).eq('id', selected.id);
      setSuccess('Registration rejected.');
      setRejectReason('');
      setSelected(null);
      await load();
    } catch(e) {
      setError(e.message || 'Rejection failed');
    } finally {
      setSaving(false);
    }
  };

  const COUNTS = {
    all:      accounts.length,
    pending:  accounts.filter(a => a.verification_status === 'pending').length,
    approved: accounts.filter(a => a.verification_status === 'approved').length,
    rejected: accounts.filter(a => a.verification_status === 'rejected').length,
  };

  const inp = { width:'100%', padding:'10px 14px', borderRadius:10, border:'1.5px solid #E2E8F0', fontSize:13, boxSizing:'border-box', outline:'none' };

  return (
    <AdminLayout>
      <div style={{ paddingBottom:40 }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
          <div>
            <h1 style={{ fontSize:24, fontWeight:800, color:'#1E293B', margin:0 }}>LMS Institutions</h1>
            <p style={{ color:'#94A3B8', fontSize:13, margin:'4px 0 0' }}>
              {COUNTS.pending > 0 && `⚠️ ${COUNTS.pending} pending · `}
              {COUNTS.all} total registrations
            </p>
          </div>
          <button onClick={load} style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 16px', borderRadius:10, border:'1px solid #E2E8F0', background:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', color:'#374151' }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Alerts */}
        {success && (
          <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:12, padding:'14px 18px', marginBottom:20, whiteSpace:'pre-line', fontSize:13, color:'#065F46', fontWeight:600 }}>
            {success}
            <button onClick={() => setSuccess('')} style={{ float:'right', background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:18 }}>✕</button>
          </div>
        )}
        {error && (
          <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:12, padding:'14px 18px', marginBottom:20, fontSize:13, color:'#991B1B', display:'flex', gap:8, alignItems:'flex-start' }}>
            <AlertCircle size={16} style={{ flexShrink:0, marginTop:1 }} />
            <span>{error}</span>
            <button onClick={() => setError('')} style={{ marginLeft:'auto', background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:18, lineHeight:1 }}>✕</button>
          </div>
        )}

        {/* Stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14, marginBottom:24 }}>
          {[
            { label:'Total',    value:COUNTS.all,      color:'#4F8EF7', emoji:'🏫', f:'' },
            { label:'Pending',  value:COUNTS.pending,  color:'#F59E0B', emoji:'⏳', f:'pending' },
            { label:'Approved', value:COUNTS.approved, color:'#10B981', emoji:'✅', f:'approved' },
            { label:'Rejected', value:COUNTS.rejected, color:'#EF4444', emoji:'❌', f:'rejected' },
          ].map(c => (
            <div key={c.label} onClick={() => setStatusF(statusF === c.f ? '' : c.f)}
              style={{ background:'#fff', borderRadius:14, border:`2px solid ${statusF===c.f ? c.color : '#E2E8F0'}`, padding:'16px 18px', cursor:'pointer', display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:24 }}>{c.emoji}</span>
              <div>
                <div style={{ fontSize:22, fontWeight:900, color:'#1E293B' }}>{c.value}</div>
                <div style={{ fontSize:12, color:'#94A3B8' }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div style={{ background:'#fff', borderRadius:14, border:'1px solid #E2E8F0', padding:16, marginBottom:20, display:'flex', gap:12, flexWrap:'wrap' }}>
          <div style={{ position:'relative', flex:1, minWidth:200 }}>
            <Search size={15} color="#9CA3AF" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }} />
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search institution, email, username..."
              style={{ width:'100%', padding:'9px 9px 9px 32px', borderRadius:8, border:'1.5px solid #E2E8F0', fontSize:13, outline:'none', boxSizing:'border-box' }} />
          </div>
          <select value={statusF} onChange={e=>setStatusF(e.target.value)}
            style={{ padding:'9px 12px', borderRadius:8, border:'1.5px solid #E2E8F0', fontSize:13, outline:'none', color:'#374151' }}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {/* Table */}
        <div style={{ background:'#fff', borderRadius:16, border:'1px solid #E2E8F0', overflow:'hidden' }}>
          {loading ? (
            <div style={{ padding:48, textAlign:'center', color:'#94A3B8' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:60, textAlign:'center' }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🏫</div>
              <div style={{ fontSize:15, fontWeight:700, color:'#374151', marginBottom:6 }}>No registrations yet</div>
              <div style={{ fontSize:13, color:'#94A3B8' }}>Institutions register at /lms/register</div>
            </div>
          ) : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#F8FAFF', borderBottom:'1px solid #E2E8F0' }}>
                  {['Institution','Contact','Username','Document','Status','Submitted','Actions'].map(h => (
                    <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:'#94A3B8', letterSpacing:0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => {
                  const sc = STATUS_COLORS[a.verification_status] || STATUS_COLORS.pending;
                  return (
                    <tr key={a.id} style={{ borderBottom: i < filtered.length-1 ? '1px solid #F1F5F9' : 'none' }}>
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ fontWeight:700, color:'#1E293B', fontSize:13 }}>{a.institution_name}</div>
                        <div style={{ fontSize:11, color:'#94A3B8' }}>{a.institution_city}{a.institution_city && a.institution_country ? ' · ':''}{a.institution_country}</div>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <div style={{ fontSize:13, fontWeight:600, color:'#1E293B' }}>{a.contact_name}</div>
                        <div style={{ fontSize:11, color:'#94A3B8' }}>{a.contact_email}</div>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <code style={{ fontSize:12, background:'#F1F5F9', padding:'3px 8px', borderRadius:5, color:'#374151', fontWeight:700 }}>
                          {a.lms_username || '—'}
                        </code>
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        {a.verification_document_url ? (
                          <a href={a.verification_document_url} target="_blank" rel="noreferrer"
                            style={{ display:'inline-flex', alignItems:'center', gap:5, color:'#4F8EF7', fontSize:12, fontWeight:600, textDecoration:'none', padding:'5px 10px', background:'#EFF6FF', borderRadius:6, border:'1px solid #BFDBFE' }}>
                            <FileText size={13} /> View
                          </a>
                        ) : (
                          <span style={{ color:'#CBD5E1', fontSize:12 }}>No doc</span>
                        )}
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <span style={{ background:sc.bg, color:sc.color, border:`1px solid ${sc.border}`, padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700 }}>
                          {sc.label}
                        </span>
                      </td>
                      <td style={{ padding:'14px 16px', fontSize:12, color:'#94A3B8' }}>
                        {new Date(a.created_at).toLocaleDateString('en-US',{ month:'short', day:'numeric', year:'numeric' })}
                      </td>
                      <td style={{ padding:'14px 16px' }}>
                        <button onClick={() => { setSelected(a); setError(''); setSuccess(''); setCustomPwd(''); setRejectReason(''); }}
                          style={{ padding:'7px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'#F8FAFF', cursor:'pointer', fontSize:12, fontWeight:600, color:'#374151', display:'flex', alignItems:'center', gap:5 }}>
                          <Eye size={13} /> Review
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Review Modal */}
        {selected && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
            <div style={{ background:'#fff', borderRadius:24, width:'100%', maxWidth:620, maxHeight:'92vh', overflowY:'auto' }}>

              <div style={{ padding:'20px 24px', borderBottom:'1px solid #E2E8F0', display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:'#fff', zIndex:1 }}>
                <div>
                  <h3 style={{ margin:0, fontWeight:800, color:'#1E293B', fontSize:18 }}>Review Registration</h3>
                  <div style={{ fontSize:13, color:'#94A3B8', marginTop:2 }}>{selected.institution_name}</div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:22, color:'#94A3B8' }}>✕</button>
              </div>

              <div style={{ padding:24 }}>

                {/* Alerts inside modal */}
                {success && (
                  <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:12, padding:'14px 18px', marginBottom:16, whiteSpace:'pre-line', fontSize:13, color:'#065F46', fontWeight:600 }}>
                    {success}
                  </div>
                )}
                {error && (
                  <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:12, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#991B1B' }}>
                    {error}
                  </div>
                )}

                {/* Institution Info */}
                <div style={{ background:'#F8FAFF', borderRadius:14, padding:16, marginBottom:16 }}>
                  <div style={{ fontWeight:700, color:'#1E293B', fontSize:13, marginBottom:10 }}>🏫 Institution</div>
                  {[
                    ['Name',     selected.institution_name],
                    ['Category', selected.institution_category || '—'],
                    ['Location', `${selected.institution_city} · ${selected.institution_country}`],
                  ].map(([l,v]) => (
                    <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #E2E8F0', fontSize:13 }}>
                      <span style={{ color:'#6B7280' }}>{l}</span>
                      <span style={{ fontWeight:600, color:'#1E293B' }}>{v}</span>
                    </div>
                  ))}
                </div>

                {/* Contact Info */}
                <div style={{ background:'#F8FAFF', borderRadius:14, padding:16, marginBottom:16 }}>
                  <div style={{ fontWeight:700, color:'#1E293B', fontSize:13, marginBottom:10 }}>👤 Contact</div>
                  {[
                    ['Name',     selected.contact_name  || '—'],
                    ['Email',    selected.contact_email || '—'],
                    ['Phone',    selected.contact_phone || '—'],
                    ['Username', selected.lms_username  || '—'],
                  ].map(([l,v]) => (
                    <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid #E2E8F0', fontSize:13 }}>
                      <span style={{ color:'#6B7280' }}>{l}</span>
                      <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                        <span style={{ fontWeight:600, color:'#1E293B' }}>{v}</span>
                        {l === 'Username' && v !== '—' && (
                          <button onClick={() => copyText(v,'uname')} style={{ background:'none', border:'none', cursor:'pointer', padding:2 }}>
                            <Copy size={12} color={copied==='uname' ? '#10B981' : '#9CA3AF'} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Document */}
                {selected.verification_document_url && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontWeight:700, color:'#1E293B', fontSize:13, marginBottom:8 }}>📄 Verification Document</div>
                    <a href={selected.verification_document_url} target="_blank" rel="noreferrer"
                      style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px', background:'#EFF6FF', border:'1.5px solid #BFDBFE', borderRadius:12, textDecoration:'none' }}>
                      <FileText size={22} color="#3B82F6" />
                      <div style={{ flex:1 }}>
                        <div style={{ color:'#1D4ED8', fontWeight:700, fontSize:13 }}>{selected.verification_document_name || 'View Document'}</div>
                        <div style={{ color:'#3B82F6', fontSize:11 }}>Click to open in new tab</div>
                      </div>
                      <ExternalLink size={14} color="#3B82F6" />
                    </a>
                  </div>
                )}

                {/* Approved credentials */}
                {selected.verification_status === 'approved' && selected.assigned_password_hint && (
                  <div style={{ background:'#F0FDF4', border:'1.5px solid #BBF7D0', borderRadius:14, padding:16, marginBottom:16 }}>
                    <div style={{ fontWeight:800, color:'#065F46', fontSize:14, marginBottom:12 }}>✅ Approved — Login Credentials</div>
                    {[
                      ['Username', selected.assigned_username || selected.lms_username, 'u2'],
                      ['Password', selected.assigned_password_hint, 'p2'],
                      ['Email',    selected.contact_email, 'e2'],
                    ].map(([l,v,k]) => (
                      <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #D1FAE5', fontSize:13 }}>
                        <span style={{ color:'#065F46' }}>{l}</span>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <code style={{ background:'#D1FAE5', padding:'3px 10px', borderRadius:6, fontWeight:800, color:'#065F46' }}>{v}</code>
                          <button onClick={() => copyText(v,k)} style={{ background:'none', border:'none', cursor:'pointer' }}>
                            <Copy size={13} color={copied===k ? '#10B981' : '#6B7280'} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop:12, background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#92400E' }}>
                      📧 Email these credentials to <strong>{selected.contact_email}</strong> — institution logs in at <strong>/lms/login</strong> using username + password
                    </div>
                  </div>
                )}

                {/* Rejected */}
                {selected.verification_status === 'rejected' && (
                  <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:12, padding:14, marginBottom:16 }}>
                    <div style={{ fontWeight:700, color:'#991B1B', fontSize:13, marginBottom:6 }}>❌ Rejected</div>
                    <div style={{ fontSize:13, color:'#991B1B' }}>{selected.rejection_reason}</div>
                  </div>
                )}

                {/* Actions — pending only */}
                {['pending','under_review'].includes(selected.verification_status) && (
                  <div style={{ borderTop:'1px solid #E2E8F0', paddingTop:20 }}>
                    <div style={{ fontWeight:800, color:'#1E293B', fontSize:14, marginBottom:14 }}>⚙️ Admin Actions</div>

                    <div style={{ marginBottom:14 }}>
                      <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>
                        Password (leave blank = auto-generate)
                      </label>
                      <input value={customPwd} onChange={e=>setCustomPwd(e.target.value)}
                        placeholder="e.g. LMS@Punjab2025 (min 8 chars)"
                        style={inp} />
                      <div style={{ fontSize:11, color:'#6B7280', marginTop:4 }}>
                        Username (unique, auto-generated): <strong style={{ color:'#1E293B' }}>{selected.lms_username}</strong>
                      </div>
                    </div>

                    <button onClick={handleApprove} disabled={saving}
                      style={{ width:'100%', padding:'13px 0', borderRadius:12, border:'none', background: saving ? '#E2E8F0' : 'linear-gradient(135deg,#10B981,#059669)', color: saving ? '#94A3B8' : '#fff', fontWeight:700, fontSize:14, cursor: saving ? 'wait' : 'pointer', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                      <CheckCircle size={16} />
                      {saving ? 'Creating account...' : 'Approve & Create Login Account'}
                    </button>

                    <div style={{ borderTop:'1px solid #F1F5F9', paddingTop:16 }}>
                      <label style={{ fontSize:12, fontWeight:700, color:'#374151', display:'block', marginBottom:5 }}>
                        Rejection Reason (required to reject)
                      </label>
                      <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)}
                        placeholder="e.g. Document is not valid. Please upload HEC recognition letter."
                        rows={3}
                        style={{ ...inp, resize:'vertical', marginBottom:10 }}
                      />
                      <button onClick={handleReject} disabled={saving || !rejectReason.trim()}
                        style={{ width:'100%', padding:'11px 0', borderRadius:10, border:'1px solid #FECACA', background:'#FEF2F2', color:'#EF4444', fontWeight:700, fontSize:13, cursor: saving || !rejectReason.trim() ? 'not-allowed' : 'pointer', opacity: !rejectReason.trim() ? 0.5 : 1 }}>
                        ✕ Reject Registration
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}